import type {
  NotificationChannel,
  NotificationEntityType,
  NotificationKind,
  NotificationPreference,
} from "../types/notifications";

/**
 * Where a notification opens, as a route and its search parameters.
 *
 * A task, an order and a customer have no page of their own — their lists open
 * one in a drawer when the URL carries `?record=<id>` — so the link is the list
 * with that parameter, the same address the record opened by hand has. Shared
 * because the bell draws it as a router link and a push message carries it as a
 * path: two copies would send the same notification to different places.
 *
 * A record type this does not know opens the dashboard rather than nowhere. Teach
 * this a new entity type the day `notify()` learns it. Following a link still
 * passes the route's permission check; this never decides who may see a record.
 */
export function notificationTarget(notification: {
  entityType: NotificationEntityType;
  entityId: string;
}): { to: string; search?: Record<string, string> } {
  switch (notification.entityType) {
    case "task":
      return { to: "/tasks/all", search: { record: notification.entityId } };
    case "task_list":
      // A morning summary points at the list, not one task.
      return { to: "/tasks/all" };
    case "order":
      return { to: "/orders", search: { record: notification.entityId } };
    case "customer":
      return {
        to: "/contacts/customers",
        search: { record: notification.entityId },
      };
    case "escalation":
      return {
        to: "/orders/escalations",
        search: { record: notification.entityId },
      };
    default:
      return { to: "/" };
  }
}

/** `notificationTarget` as the path a push message carries: `/tasks/all?record=12`. */
export function notificationPath(notification: {
  entityType: NotificationEntityType;
  entityId: string;
}): string {
  const { to, search } = notificationTarget(notification);
  if (!search) return to;
  const query = Object.entries(search)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
  return `${to}?${query}`;
}

/**
 * Every kind of notification, with the words the preferences page shows.
 *
 * Here rather than in either application because both need the same list: the
 * API validates a preference against it and fills in defaults from it, and the
 * web draws one row per entry. Two copies would drift, and a kind on one side
 * only is a switch that does nothing.
 */
export const NOTIFICATION_KINDS: readonly {
  kind: NotificationKind;
  label: string;
  description: string;
}[] = [
  {
    kind: "task_assigned",
    label: "Assigned to you",
    description: "A task is assigned to you.",
  },
  {
    kind: "task_mentioned",
    label: "Mentioned",
    description: "Someone mentions you in a task comment.",
  },
  {
    kind: "task_commented",
    label: "Comments on your tasks",
    description:
      "Someone comments on a task you created, are assigned to or watch.",
  },
  {
    kind: "task_status_changed",
    label: "Status changes on your tasks",
    description:
      "The status changes on a task you created, are assigned to or watch.",
  },
  {
    kind: "comment_mentioned",
    label: "Mentioned on an order or customer",
    description: "Someone mentions you in a comment on an order or a customer.",
  },
  {
    kind: "morning_summary",
    label: "Morning summary",
    description:
      "Once a day, at the morning-summary time: your tasks due today and overdue.",
  },
];

/**
 * The stored sentence for a kind, from how the record is named alone — a task's
 * `OPS-142`, an order's `order #1001`, a customer's `customer 123`: an
 * identifier each, never a name.
 *
 * `notify()` writes this and nothing the caller supplies, so a notification can
 * never carry a customer's name, an address or a comment's words — the row is
 * read into push messages and email subjects later, and both pass through other
 * companies' servers. The fixture adapter uses the same function, so the two
 * implementations of the interface say the same thing.
 */
export function notificationText(
  kind: NotificationKind,
  reference: string,
  /** Only the morning summary has one: its counts and task references. */
  detail?: string,
): string {
  switch (kind) {
    case "task_reminder":
      return `Reminder: ${reference}`;
    case "morning_summary":
      return detail ? `Your tasks today: ${detail}` : "Your tasks today";
    case "task_assigned":
      return `You were assigned ${reference}`;
    case "task_mentioned":
      return `You were mentioned on ${reference}`;
    case "task_commented":
      return `New comment on ${reference}`;
    case "task_status_changed":
      return `Status changed on ${reference}`;
    case "comment_mentioned":
      return `You were mentioned on ${reference}`;
  }
}

/**
 * The kinds whose email goes out at once; every other kind that is on for email
 * waits for the daily digest (#446: "mentions and assignments are sent at once;
 * everything else goes into one daily digest"). A person is waiting on these
 * three — someone asked for something or named them — and the rest is
 * background they can read in the morning.
 */
const EMAIL_AT_ONCE: readonly NotificationKind[] = [
  "task_assigned",
  "task_mentioned",
  "comment_mentioned",
];

export type EmailDelivery = "at_once" | "digest";

/** How a kind's email is sent, once the person has it switched on. */
export function emailDelivery(kind: NotificationKind): EmailDelivery {
  return EMAIL_AT_ONCE.includes(kind) ? "at_once" : "digest";
}

export const NOTIFICATION_CHANNELS: readonly {
  channel: NotificationChannel;
  label: string;
}[] = [
  { channel: "in_app", label: "In the app" },
  { channel: "email", label: "Email" },
  { channel: "push", label: "Phone push" },
];

export function isNotificationKind(value: unknown): value is NotificationKind {
  return NOTIFICATION_KINDS.some((entry) => entry.kind === value);
}

export function isNotificationChannel(
  value: unknown,
): value is NotificationChannel {
  return NOTIFICATION_CHANNELS.some((entry) => entry.channel === value);
}

/**
 * Whether a channel is on when the person has never chosen: yes, for every kind
 * (#446: "on by default"). One constant rather than a function of kind and
 * channel until some kind needs a different default — at which point this is
 * the line that becomes a lookup.
 */
const ENABLED_BY_DEFAULT = true;

/**
 * The full kind × channel grid, from what a person has overridden.
 *
 * `stored` holds only choices someone made; everything else takes its default,
 * and the bell is forced on — "the bell is always on", which is why `in_app` is
 * never something a person can switch off. Used by the API to answer, and by
 * the fixture adapter to do the same, so the two cannot disagree about what
 * "unset" means.
 */
export function resolvePreferences(
  stored: readonly NotificationPreference[],
): NotificationPreference[] {
  const overrides = new Map(
    stored.map((row) => [`${row.kind}:${row.channel}`, row.enabled] as const),
  );
  const grid: NotificationPreference[] = [];
  for (const { kind } of NOTIFICATION_KINDS) {
    for (const { channel } of NOTIFICATION_CHANNELS) {
      grid.push({
        kind,
        channel,
        enabled:
          channel === "in_app"
            ? true
            : (overrides.get(`${kind}:${channel}`) ?? ENABLED_BY_DEFAULT),
      });
    }
  }
  return grid;
}
